import { arrayBufferToBase64 } from '@qdabra/infopath-attachments';
import { FILE_NAME_KEY, FILE_SIZE_KEY } from '../../constants/file.constants';
import { getNodeData, setNodeData } from '../../utility/nodeDataHelpers';
import attachmentClickHandler from '../Controls/attachmentClickHandler';
import handleSharePointFileDeleteClickAsync from '../Controls/removeAttachmentClickHandler';
import { CLOSE } from '../Rules/resultTypes';
import { DIALOG_RESULT_RETRIEVED } from '../UI/dialog.constants';
import fileDialog from '../UI/fileDialog';
import formCloser from '../UI/formCloser';
import promptLinkedImage from '../uiHelpers/linkedImagePrompt';

const makeButtonHandler = ({
    template,
    viewManager,
    getNodeForNodeId,
    runRuleSetAsync,
    // renamed because if it begins with "use", eslint thinks it's a hook
    useLoader: withLoader,
    renderViewAsync,
    renderTarget,
    dataConnections,
    dataSources,
    shpAccess,
}) => {
    function closeForm() {
        const closer = formCloser(renderTarget);
        closer.closeForm();
    }

    // String, String, String -> RuleSetResult?
    async function executeButtonRulesAsync(ctrlId, nodeId) {
        const ruleSet = template.getButtonRules(
            viewManager.getCurrentView(),
            ctrlId,
        );
        const context = ruleSet && getNodeForNodeId(nodeId);

        if (ruleSet && context) {
            return await runRuleSetAsync(ruleSet, context);
        }

        // TODO?: log info
        return null;
    }

    async function pickImageAsync(ctrlId, nodeId) {
        const contextNode = getNodeForNodeId(nodeId);

        if (!contextNode) {
            throw new Error(
                `Could not find node for clicked image. CtrlId: ${ctrlId}; id: ${nodeId}`,
            );
        }

        const result = await fileDialog().showAsync();

        if (result.result === DIALOG_RESULT_RETRIEVED) {
            const b64 = arrayBufferToBase64(result.file.bytes);

            await contextNode.setValueAsync(b64);
        }
    }

    function resetFileValueCacheValues(field) {
        if (getNodeData(field, FILE_NAME_KEY)) {
            setNodeData(field, FILE_NAME_KEY, null);
            setNodeData(field, FILE_SIZE_KEY, null);
        }
    }

    function setNodeValueAsync(node) {
        resetFileValueCacheValues(node);
        // TODO: implement
    }

    function createObjectModel() {
        return {
            getNodeById: getNodeForNodeId,
            setNodeValueAsync,
        };
    }

    async function handleFileClickAsync(
        nodeId,
        targetControlType,
        disableEditing,
    ) {
        const om = createObjectModel();
        const handler = attachmentClickHandler(
            om,
            nodeId,
            targetControlType,
            disableEditing,
        );

        await handler.handleClickAsync();

        return renderViewAsync();
    }

    async function handleSharePointFileRemoveAsync(nodeId) {
        const field = getNodeForNodeId(nodeId);

        await handleSharePointFileDeleteClickAsync(shpAccess, field);

        await renderViewAsync();
    }

    async function handleLinkedImageClickAsync(nodeId) {
        if (await promptLinkedImage(nodeId, dataSources)) {
            await renderViewAsync();
        }
    }

    // DataAdapter -> Promise[void]
    async function submitWithAsync(adapter) {
        const name = adapter.name;
        const conn = dataConnections.get(name);

        if (!conn) {
            throw new Error(`Data connection not available: ${name}`);
        }

        return conn.submitAsync();
    }

    async function performDefaultSubmitAsync() {
        var submitSettings = template.getSubmitSettings();

        if (!submitSettings) {
            throw new Error(
                'This form does not have any submit settings specified.',
            );
        }

        const { adapter, ruleSet } = submitSettings;

        if (adapter) {
            return submitWithAsync(adapter);
        }
        if (ruleSet) {
            return runRuleSetAsync(ruleSet);
        }

        throw new Error(
            "This form's default submit action is not currently supported.",
        );
    }

    async function performMainQueryAsync() {
        const mainQuery = dataConnections.getMainQuery();

        if (!mainQuery) {
            throw new Error('This form does not have a main query connection');
        }

        return mainQuery.queryAsync();
    }

    async function handleRuleSetResultAsync(ruleSetResult) {
        if (ruleSetResult && ruleSetResult.status === CLOSE) {
            closeForm();
            return;
        }

        await renderViewAsync();
    }

    function handleButtonAction({ props }) {
        const action = props.action || '';
        const { ctrlId, nodeId, controlType, target, disableEditing } = props;

        switch (action) {
            case '':
                return withLoader(
                    executeButtonRulesAsync(ctrlId, nodeId).then(
                        handleRuleSetResultAsync,
                    ),
                );
            case 'submit':
                return withLoader(
                    performDefaultSubmitAsync().then(renderViewAsync),
                );
            case 'query':
                return withLoader(
                    performMainQueryAsync().then(renderViewAsync),
                );
            case 'image':
                return (
                    pickImageAsync(ctrlId, nodeId)
                        // TODO (perf): only re-render if an image was picked
                        .then(renderViewAsync)
                );
            case 'file':
                return handleFileClickAsync(
                    nodeId,
                    controlType,
                    disableEditing,
                );
            case 'removefile':
                return handleSharePointFileRemoveAsync(nodeId);
            case 'linkedimage':
                return handleLinkedImageClickAsync(nodeId, target);
            default:
                return Promise.reject(new Error('Unknown action: ' + action));
        }
    }

    return handleButtonAction;
};

export default makeButtonHandler;
